import React from 'react';
import { render } from 'react-dom';
import { Recommendations, ApplicationErrorBoundary } from 'components';
import { Provider } from 'react-redux';
import globalStore from 'state/configure-store';
import { inViewport } from 'packages';
import { LoadingSymbolCards } from 'static/script/components/recommendations/recommendation-loading-spinners';
import { recommendationsV3RequestBuilder } from 'components/recommendations/recommendation-request';
import ContentSwitch from 'components/recommendations/content-switch';
import { MultiCurrencyBoot } from 'components';

import 'components/recommendations/recommendations.scss';

import ArrowRightIcon from '@tradera/tradera-ui/icons/arrow-right.svg?jsx';

const EndingSoonErrorMessage = () => (
    <div className="col-12 text-center pt-2 pb-4">
        <p className="h5 mb-3">
            <strong>Hoppsan, nu gick något fel.</strong>
        </p>
        <p className="pb-2 mb-4">Testa igen om en stund eller...</p>
        <div className="col-12 text-center">
            <a
                data-recommendations-reload
                role="button"
                className="btn btn-primary btn-shadow text-white h6"
                href="/campaign/sista-chansen">
                Gå till sista chansen
                <ArrowRightIcon className="icon icon-sm icon-white ml-3" />
            </a>
        </div>
    </div>
);

const LoadmoreButton = ({ queryString }) => (
    <div className="col-12 text-center">
        <a
            data-load-more-recommendations
            role="button"
            className="btn btn-primary btn-wide btn-shadow text-white h5"
            href={`/ma/endless-recommendation?${queryString}`}>
            <div className="row justify-content-between align-items-center">
                <div className="col" />
                <div className="col">Visa fler</div>
                <div className="col d-flex flex-row-reverse">
                    <ArrowRightIcon className="icon" />
                </div>
            </div>
        </a>
    </div>
);

const StartpageRecommendationOriginal = ({
    itemsPerPage,
    isLoggedIn,
    isQuickViewEnabled
}) => (
    <ApplicationErrorBoundary>
        <Provider store={globalStore}>
            <Recommendations
                Title={
                    <h2 className="text-branded my-4">
                        {isLoggedIn ? 'Bara för dig' : 'Trendar på Tradera'}
                    </h2>
                }
                itemsPerPage={itemsPerPage}
                createRequestUrl={recommendationsV3RequestBuilder(
                    'Infinite_scroll_recommendation_slot',
                    itemsPerPage
                )}
                LoadingSymbol={
                    <LoadingSymbolCards itemsPerPage={itemsPerPage} />
                }
                itm={{
                    source: 'startpage',
                    medium: 'suggest-from-blueshift'
                }}
                collapseIfEmpty={true}
                enableQuickView={isQuickViewEnabled}>
                <LoadmoreButton queryString="selected=utvalt" />
            </Recommendations>
        </Provider>
    </ApplicationErrorBoundary>
);

class StartPageRecommendation extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false
        };
    }

    render() {
        const { itemsPerPage, isLoggedIn, isQuickViewEnabled } = this.props;
        const { hasError } = this.state;

        return hasError ? null : (
            <ApplicationErrorBoundary>
                <section>
                    <h2 className="text-branded text-center my-4">
                        {isLoggedIn ? 'Bara för dig' : 'Trendar på Tradera'}
                    </h2>
                    <ContentSwitch
                        defaultSelected={'utvalt'}
                        options={{
                            utvalt: {
                                node: (
                                    <Provider store={globalStore}>
                                        <Recommendations
                                            itemsPerPage={itemsPerPage}
                                            collapseIfEmpty={false}
                                            createRequestUrl={recommendationsV3RequestBuilder(
                                                'Infinite_scroll_recommendation_slot',
                                                itemsPerPage
                                            )}
                                            LoadingSymbol={
                                                <LoadingSymbolCards
                                                    itemsPerPage={itemsPerPage}
                                                />
                                            }
                                            itm={{
                                                source: 'startpage',
                                                medium: 'suggest-from-blueshift'
                                            }}
                                            enableQuickView={isQuickViewEnabled}
                                            onEnteringErrorState={() =>
                                                this.setState({
                                                    hasError: true
                                                })
                                            }>
                                            <LoadmoreButton queryString="selected=utvalt" />
                                        </Recommendations>
                                    </Provider>
                                ),
                                label: 'Utvalt'
                            },
                            kort_tid_kvar: {
                                node: (
                                    <ApplicationErrorBoundary
                                        fallback={<EndingSoonErrorMessage />}>
                                        <Provider store={globalStore}>
                                            <Recommendations
                                                itemsPerPage={itemsPerPage}
                                                collapseIfEmpty={false}
                                                createRequestUrl={recommendationsV3RequestBuilder(
                                                    'Last_chance_infinite_scroll',
                                                    itemsPerPage
                                                )}
                                                LoadingSymbol={
                                                    <LoadingSymbolCards
                                                        itemsPerPage={
                                                            itemsPerPage
                                                        }
                                                    />
                                                }
                                                itm={{
                                                    source: 'startpage',
                                                    medium:
                                                        'ending-soon-from-blueshift'
                                                }}
                                                ErrorMessage={
                                                    <EndingSoonErrorMessage />
                                                }
                                                enableQuickView={
                                                    isQuickViewEnabled
                                                }>
                                                <LoadmoreButton queryString="selected=kort_tid_kvar" />
                                            </Recommendations>
                                        </Provider>
                                    </ApplicationErrorBoundary>
                                ),
                                label: (
                                    <span>
                                        Kort tid kvar
                                        <span className="h6">
                                            <span className="ml-1 badge badge-success">
                                                NY!
                                            </span>
                                        </span>
                                    </span>
                                )
                            }
                        }}
                    />
                </section>
            </ApplicationErrorBoundary>
        );
    }
}

export default initData => {
    const selector = 'product-recommendations';
    const element = document.getElementById(selector);
    const isLoggedIn = initData.isLoggedIn;
    const isQuickViewEnabled = !!initData.featureSwitches['quick-view'];
    const isEndingSoonEnabled = !!initData.featureSwitches[
        'ending-soon-from-blueshift'
    ];

    if (element) {
        const mount = target =>
            render(
                isEndingSoonEnabled ? (
                    <StartPageRecommendation
                        itemsPerPage={12}
                        isLoggedIn={isLoggedIn}
                        isQuickViewEnabled={isQuickViewEnabled}
                    />
                ) : (
                    <StartpageRecommendationOriginal
                        itemsPerPage={12}
                        isLoggedIn={isLoggedIn}
                        isQuickViewEnabled={isQuickViewEnabled}
                    />
                ),
                target
            );
        inViewport(`#${selector}`, mount);
    }

    MultiCurrencyBoot.boot(initData);
};



// WEBPACK FOOTER //
// ./src/EbaySweden.TouchWeb/static/script/app/pages/start-page/startpage-recommendations-mount.js